/**
 * Compteur de joueurs en ligne, par la présence Realtime de Supabase.
 *
 * **Aucune table, aucune écriture, aucune donnée personnelle.** La présence Realtime
 * maintient l'état des connexions ouvertes côté serveur et le diffuse aux abonnés : on
 * s'inscrit sur un canal, on suit les entrées et sorties, et on compte. Une table de
 * « joueurs connectés » aurait imposé une écriture par battement de cœur, un job de
 * nettoyage des sessions mortes, et une ligne par visiteur de passage — pour un nombre
 * qu'on ne conserve même pas.
 *
 * **Ce qui transite est un identifiant éphémère, tiré au hasard à chaque session.** Il
 * n'est lié à aucun compte, ne survit pas à un rechargement, et n'est jamais enregistré.
 * Le §13 interdit de faire transiter autre chose qu'un pseudonyme ; ici, même le
 * pseudonyme est jetable.
 *
 * **La dégradation est silencieuse et totale.** Sans configuration Supabase, sans
 * réseau, ou si le canal tombe, le compteur rend simplement `null` et l'interface
 * n'affiche rien. Un jeu qui refuse de démarrer parce qu'un compteur décoratif échoue
 * serait une régression, pas une fonctionnalité.
 */

import { getSupabase, isSupabaseConfigured } from './supabase';

/**
 * Canal par défaut : le lobby global, tous joueurs confondus.
 *
 * ⚠️ Il n'est plus le SEUL. La page publique d'une ville ouvre son propre canal
 * (`villopia:city:<slug>`) pour compter qui la regarde en ce moment (§9, ROADMAP 7.7).
 * Deux compteurs, deux questions différentes — « combien jouent » et « combien sont
 * ICI » — et les mélanger afficherait le trafic du jeu entier sur la page d'une ville.
 */
const DEFAULT_CHANNEL = 'villopia:lobby';

/** Canal d'une ville donnée. Le slug est déjà public : il est dans l'URL. */
export function cityChannel(slug: string): string {
  return `villopia:city:${slug}`;
}

export interface PresenceHandle {
  /** Nombre de joueurs connectés, ou `null` tant qu'on ne sait pas. */
  count(): number | null;
  destroy(): void;
}

export interface PresenceOptions {
  /** Appelé à chaque changement, y compris au passage à `null`. */
  readonly onChange?: (count: number | null) => void;
  /** Canal à rejoindre. Par défaut le lobby global. */
  readonly channel?: string;
}

/**
 * Identifiant de session, jetable.
 *
 * `crypto.randomUUID` plutôt qu'un compteur ou un horodatage : deux onglets ouverts à
 * la même milliseconde doivent compter pour deux, et un identifiant devinable
 * permettrait d'usurper la présence d'autrui.
 */
function sessionKey(): string {
  return globalThis.crypto.randomUUID();
}

export function createPresence(options: PresenceOptions = {}): PresenceHandle {
  let current: number | null = null;

  const publish = (next: number | null): void => {
    if (next === current) return;
    current = next;
    options.onChange?.(next);
  };

  // Sans configuration, on ne tente rien : une erreur réseau au démarrage retarderait
  // le premier rendu pour un compteur décoratif.
  if (!isSupabaseConfigured()) {
    return { count: () => null, destroy: () => undefined };
  }

  let channel: ReturnType<VillopiaChannel> | null = null;

  try {
    const key = sessionKey();
    const supabase = getSupabase();

    channel = supabase.channel(options.channel ?? DEFAULT_CHANNEL, {
      config: { presence: { key } },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        // `presenceState()` rend un objet clé → liste de métadonnées. On compte les
        // CLÉS et non les entrées : un même onglet peut apparaître plusieurs fois
        // pendant une reconnexion, et le joueur ne compte qu'une fois.
        const state = channel?.presenceState() ?? {};
        publish(Object.keys(state).length);
      })
      .subscribe((status) => {
        /*
          Comparaison sur la chaîne et non sur l'énumération de Supabase.

          `REALTIME_SUBSCRIBE_STATES` n'est pas exporté par la bibliothèque : s'y
          référer par son type lierait ce fichier à un détail interne, et une montée de
          version pourrait le casser sans prévenir. Les valeurs, elles, sont stables —
          ce sont celles que le protocole transporte.
        */
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        if (status === 'SUBSCRIBED') {
          // La charge est vide : on ne diffuse rien sur soi, seule la connexion compte.
          void channel?.track({});
          return;
        }

        /*
          Tout autre statut — fermé, en erreur, expiré — signifie qu'on ne sait plus
          combien de joueurs sont là. On cesse alors d'affirmer un nombre plutôt que de
          laisser le dernier connu à l'écran, où il vieillirait sans que rien ne le dise.
        */
        publish(null);
      });
  } catch {
    // Configuration présente mais invalide : même dégradation, sans bruit.
    publish(null);
  }

  return {
    count: () => current,
    destroy() {
      try {
        void channel?.unsubscribe();
      } catch {
        // Se désabonner d'un canal déjà tombé ne doit pas remonter jusqu'au joueur.
      }
      channel = null;
    },
  };
}

/** Alias de type pour rester lisible sans importer les types internes de Supabase. */
type VillopiaChannel = ReturnType<typeof getSupabase>['channel'];
